import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import BotProtection from "@/components/BotProtection";
import ConsentAgreement from "@/components/ConsentAgreement";
import { formatApiError } from "@/utils/apiError";
import { isEmailFilled, requiredFieldsFilled } from "@/utils/formReady";
import { trackLead } from "@/utils/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PreQualificationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [consent, setConsent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", address: "", dateOfBirth: "",
    jobDuration: "", monthlyIncome: "", hasAutoLoan: "", loanBalance: "",
    creditScore: "", hasCollections: "", collectionsDetails: "",
    hasOpenBankruptcies: "", bankruptciesDetails: "", hasRepossession: "",
    repossessionTime: "", interestedVehicle: "", budget: "", downPayment: "", payTaxesUpfront: ""
  });
  const canSubmit =
    consent &&
    isEmailFilled(formData.email) &&
    requiredFieldsFilled([
      formData.name,
      formData.phone,
      formData.address,
      formData.dateOfBirth,
      formData.jobDuration,
      formData.monthlyIncome,
      formData.hasAutoLoan,
      formData.creditScore,
      formData.hasCollections,
      formData.hasOpenBankruptcies,
      formData.hasRepossession,
      formData.interestedVehicle,
      formData.budget,
      formData.downPayment,
      formData.payTaxesUpfront,
    ]);

  const handleChange = (e) => { setFormData(prev => ({ ...prev, [e.target.name]: e.target.value })); };
  const handleSelectChange = (name, value) => { setFormData(prev => ({ ...prev, [name]: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("consent.formRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/prequalification`, { ...formData, consent, captcha_token: captchaToken });
      trackLead("prequalification");
      toast.success("Pre-qualification submitted successfully! Our team will review your information and contact you within 24 hours.");
      setFormData({ name: "", email: "", phone: "", address: "", dateOfBirth: "", jobDuration: "", monthlyIncome: "", hasAutoLoan: "", loanBalance: "", creditScore: "", hasCollections: "", collectionsDetails: "", hasOpenBankruptcies: "", bankruptciesDetails: "", hasRepossession: "", repossessionTime: "", interestedVehicle: "", budget: "", downPayment: "", payTaxesUpfront: "" });
      setConsent(false);
      setTimeout(() => { navigate('/'); }, 2000);
    } catch (error) {
      console.error("Pre-qualification submission error:", error);
      toast.error(formatApiError(error, "Failed to submit pre-qualification. Please try again."));
    } finally { setSubmitting(false); }
  };

  const inputCls = isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900";
  const selectTriggerCls = isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900";
  const selectContentCls = isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200";
  const selectItemCls = isDark ? "text-white hover:bg-white/10" : "text-gray-900 hover:bg-gray-50";
  const labelCls = isDark ? "text-white/80 font-['Manrope'] mb-2" : "text-gray-700 font-['Manrope'] mb-2";
  const stepBadgeCls = isDark ? "bg-blue-500" : "bg-red-600";

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`}>
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <button onClick={() => navigate('/')} className={`inline-flex items-center gap-2 transition-colors mb-6 ${isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
              <ArrowLeft className="w-4 h-4" /><span className="font-['Manrope']">{t('common.back')}</span>
            </button>
            <h1 className={`text-4xl md:text-5xl font-['Unbounded'] font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('preQual.title')}</h1>
            <p className={`text-xl font-['Manrope'] mb-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{t('preQual.description')}</p>
            <div className={`inline-flex items-center gap-2 px-4 py-2 border ${isDark ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-300'}`}>
              <CheckCircle className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              <span className={`font-['Manrope'] font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>{t('preQualCTA.noCreditImpact')}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className={`p-8 ${isDark ? 'bg-white/5 backdrop-blur-sm border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>1</span>
                  {t('preQual.step1Title')}
                </h2>
                <div><Label htmlFor="name" className={labelCls}>{t('preQual.fullName')} *</Label><Input id="name" name="name" value={formData.name} onChange={handleChange} required className={inputCls} placeholder={t('preQual.fullNamePlaceholder')} /></div>
                <div><Label htmlFor="email" className={labelCls}>{t('preQual.email')} *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className={inputCls} placeholder={t('preQual.emailPlaceholder')} /></div>
                <div><Label htmlFor="phone" className={labelCls}>{t('preQual.phone')} *</Label><Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className={inputCls} placeholder={t('preQual.phonePlaceholder')} /></div>
                <div><Label htmlFor="address" className={labelCls}>{t('preQual.address')} *</Label><Input id="address" name="address" value={formData.address} onChange={handleChange} required className={inputCls} placeholder={t('preQual.addressPlaceholder')} /></div>
                <div><Label htmlFor="dateOfBirth" className={labelCls}>{t('preQual.dob')} *</Label><Input id="dateOfBirth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} required className={inputCls} /></div>
              </div>

              {/* Employment */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>2</span>
                  {t('preQual.step2Title')}
                </h2>
                <div><Label htmlFor="jobDuration" className={labelCls}>{t('preQual.jobDuration')} *</Label><Input id="jobDuration" name="jobDuration" value={formData.jobDuration} onChange={handleChange} required className={inputCls} placeholder={t('preQual.selectDuration')} /></div>
                <div><Label htmlFor="monthlyIncome" className={labelCls}>{t('preQual.monthlyIncome')} *</Label><Input id="monthlyIncome" name="monthlyIncome" type="number" value={formData.monthlyIncome} onChange={handleChange} required className={inputCls} placeholder={t('preQual.incomePlaceholder')} /></div>
              </div>

              {/* Credit */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>3</span>
                  {t('preQual.step3Title')}
                </h2>
                <div>
                  <Label htmlFor="hasAutoLoan" className={labelCls}>{t('preQual.hasAutoLoan')} *</Label>
                  <Select value={formData.hasAutoLoan} onValueChange={(value) => handleSelectChange("hasAutoLoan", value)} required>
                    <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={t('preQual.selectScore')} /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      <SelectItem value="no" className={selectItemCls}>{t('preQual.no')}</SelectItem>
                      <SelectItem value="yes" className={selectItemCls}>{t('preQual.yes')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.hasAutoLoan === "yes" && (
                  <div><Label htmlFor="loanBalance" className={labelCls}>{t('preQual.loanBalance')}</Label><Input id="loanBalance" name="loanBalance" type="number" value={formData.loanBalance} onChange={handleChange} className={inputCls} placeholder="e.g., 15000" /></div>
                )}
                <div><Label htmlFor="creditScore" className={labelCls}>{t('preQual.creditScore')} *</Label><Input id="creditScore" name="creditScore" type="number" value={formData.creditScore} onChange={handleChange} required min="300" max="850" className={inputCls} placeholder="e.g., 680" /></div>
                <div>
                  <Label htmlFor="hasCollections" className={labelCls}>{t('preQual.hasCollections')} *</Label>
                  <Select value={formData.hasCollections} onValueChange={(value) => handleSelectChange("hasCollections", value)} required>
                    <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={t('preQual.selectScore')} /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      <SelectItem value="no" className={selectItemCls}>{t('preQual.no')}</SelectItem>
                      <SelectItem value="yes" className={selectItemCls}>{t('preQual.yes')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.hasCollections === "yes" && (
                  <div><Label htmlFor="collectionsDetails" className={labelCls}>{t('preQual.collectionsDetails')}</Label><Textarea id="collectionsDetails" name="collectionsDetails" value={formData.collectionsDetails} onChange={handleChange} rows={2} className={`${inputCls} resize-none`} placeholder="e.g., 2 years ago, medical bills" /></div>
                )}
                <div>
                  <Label htmlFor="hasOpenBankruptcies" className={labelCls}>{t('preQual.hasBankruptcies')} *</Label>
                  <Select value={formData.hasOpenBankruptcies} onValueChange={(value) => handleSelectChange("hasOpenBankruptcies", value)} required>
                    <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={t('preQual.selectScore')} /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      <SelectItem value="no" className={selectItemCls}>{t('preQual.no')}</SelectItem>
                      <SelectItem value="yes" className={selectItemCls}>{t('preQual.yes')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.hasOpenBankruptcies === "yes" && (
                  <div><Label htmlFor="bankruptciesDetails" className={labelCls}>{t('preQual.bankruptcyDetails')}</Label><Textarea id="bankruptciesDetails" name="bankruptciesDetails" value={formData.bankruptciesDetails} onChange={handleChange} rows={2} className={`${inputCls} resize-none`} placeholder="e.g., Filed 3 years ago due to business closure" /></div>
                )}
                <div>
                  <Label htmlFor="hasRepossession" className={labelCls}>{t('preQual.hasRepossession')} *</Label>
                  <Select value={formData.hasRepossession} onValueChange={(value) => handleSelectChange("hasRepossession", value)} required>
                    <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={t('preQual.selectScore')} /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      <SelectItem value="no" className={selectItemCls}>{t('preQual.no')}</SelectItem>
                      <SelectItem value="yes" className={selectItemCls}>{t('preQual.yes')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.hasRepossession === "yes" && (
                  <div><Label htmlFor="repossessionTime" className={labelCls}>{t('preQual.repossessionTime')}</Label><Input id="repossessionTime" name="repossessionTime" value={formData.repossessionTime} onChange={handleChange} className={inputCls} placeholder="e.g., 3 years ago" /></div>
                )}
              </div>

              {/* Vehicle Interest */}
              <div className="space-y-6">
                <h2 className={`text-2xl font-['Unbounded'] font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <span className={`w-8 h-8 ${stepBadgeCls} flex items-center justify-center text-sm text-white`}>4</span>
                  {t('preQual.step4Title')}
                </h2>
                <div><Label htmlFor="interestedVehicle" className={labelCls}>{t('preQual.interestedVehicle')} *</Label><Input id="interestedVehicle" name="interestedVehicle" value={formData.interestedVehicle} onChange={handleChange} required className={inputCls} placeholder={t('preQual.vehiclePlaceholder')} /></div>
                <div><Label htmlFor="budget" className={labelCls}>{t('preQual.budget')} *</Label><Input id="budget" name="budget" type="number" value={formData.budget} onChange={handleChange} required className={inputCls} placeholder={t('preQual.budgetPlaceholder')} /></div>
                <div><Label htmlFor="downPayment" className={labelCls}>{t('preQual.downPayment')} *</Label><Input id="downPayment" name="downPayment" type="number" value={formData.downPayment} onChange={handleChange} required className={inputCls} placeholder={t('preQual.downPaymentPlaceholder')} /></div>
                <div>
                  <Label htmlFor="payTaxesUpfront" className={labelCls}>{t('preQual.payTaxesUpfront')} *</Label>
                  <Select value={formData.payTaxesUpfront} onValueChange={(value) => handleSelectChange("payTaxesUpfront", value)} required>
                    <SelectTrigger className={selectTriggerCls}><SelectValue placeholder={t('preQual.selectScore')} /></SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      <SelectItem value="yes" className={selectItemCls}>{t('preQual.yes')}</SelectItem>
                      <SelectItem value="no" className={selectItemCls}>{t('preQual.no')}</SelectItem>
                      <SelectItem value="maybe" className={selectItemCls}>Maybe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className={`space-y-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-['Unbounded'] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('preQual.consentTitle')}</h3>
                <ConsentAgreement
                  id="prequal-consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  isDark={isDark}
                  testId="prequal-consent-checkbox"
                  checkboxClassName={`mt-1 ${isDark ? '' : 'border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600'}`}
                />
              </div>

              <BotProtection onToken={setCaptchaToken} isDark={isDark} />

              <div className={`pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <Button type="submit" disabled={submitting || !canSubmit}
                  className={`w-full text-white font-['Manrope'] font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-600 hover:bg-red-700'}`}>
                  {submitting ? t('preQual.submitting') : t('preQual.submitBtn')}
                </Button>
                <p className={`text-center text-sm font-['Manrope'] mt-4 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{t('preQualCTA.noCreditImpact')}</p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
